import { z } from "zod";

export const SUPPLIER_INVOICE_STATUSES = [
  "draft",
  "received",
  "pending_review",
  "approved",
  "scheduled_for_payment",
  "partially_paid",
  "paid",
  "cancelled",
] as const;

export const SUPPLIER_INVOICE_SOURCES = ["manual", "imported"] as const;

export const SUPPLIER_INVOICE_ALLOCATION_TARGETS = [
  "installation",
  "warehouse",
  "general_overhead",
  "unallocated",
] as const;

export const supplierInvoiceStatusSchema = z.enum(SUPPLIER_INVOICE_STATUSES);
export const supplierInvoiceSourceSchema = z.enum(SUPPLIER_INVOICE_SOURCES);
export const supplierInvoiceAllocationTargetSchema = z.enum(SUPPLIER_INVOICE_ALLOCATION_TARGETS);

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha invalida");
export const paymentMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Mes de pago invalido");
const currencySchema = z.string().trim().length(3).transform((value) => value.toUpperCase());

export const moneySchema = z.union([z.string(), z.number()]).transform((value, ctx) => {
  const cents = parseMoneyToCents(value);
  if (cents === null || cents < 0) {
    ctx.addIssue({ code: "custom", message: "Importe invalido" });
    return z.NEVER;
  }
  return formatCentsAsDecimal(cents);
});

const positiveMoneySchema = z.union([z.string(), z.number()]).transform((value, ctx) => {
  const cents = parseMoneyToCents(value);
  if (cents === null || cents <= 0) {
    ctx.addIssue({ code: "custom", message: "El importe debe ser mayor que cero" });
    return z.NEVER;
  }
  return formatCentsAsDecimal(cents);
});

// ─── DOCUMENT VALIDATION ───
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
] as const;

export const MAX_DOCUMENT_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
export const MAX_DOCUMENT_SIZE_BYTES_BASE64 = Math.ceil(MAX_DOCUMENT_SIZE_BYTES * 4 / 3) + 512;

export const ALLOWED_DOCUMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'] as const;

// ─── INPUT SCHEMAS ───

export const supplierInvoiceLineInputSchema = z.object({
  productId: z.number().int().positive().optional().nullable(),
  description: z.string().trim().min(1).max(1000),
  quantity: z.union([z.string(), z.number()]).transform((value, ctx) => {
    const normalized = normalizeDecimalString(value);
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      ctx.addIssue({ code: "custom", message: "Cantidad invalida" });
      return z.NEVER;
    }
    return normalized;
  }),
  unit: z.string().trim().min(1).max(40).default("ud"),
  unitPrice: moneySchema,
  taxableBase: moneySchema,
  taxRate: z.union([z.string(), z.number()]).transform((value, ctx) => {
    const normalized = normalizeDecimalString(value);
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
      ctx.addIssue({ code: "custom", message: "IVA invalido" });
      return z.NEVER;
    }
    return normalized;
  }).default("21.00"),
  vatAmount: moneySchema,
  totalAmount: moneySchema,
  financialCategoryId: z.number().int().positive().optional().nullable(),
  position: z.number().int().positive().optional(),
});

export const supplierInvoiceAllocationInputSchema = z.object({
  targetType: supplierInvoiceAllocationTargetSchema,
  targetId: z.number().int().positive().optional().nullable(),
  allocatedAmount: moneySchema,
  allocatedPercentage: z.union([z.string(), z.number()]).optional().nullable().transform((value, ctx) => {
    if (value === undefined || value === null || value === "") return null;
    const normalized = normalizeDecimalString(value);
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
      ctx.addIssue({ code: "custom", message: "Porcentaje de imputacion invalido" });
      return z.NEVER;
    }
    return normalized;
  }),
  notes: z.string().trim().max(1000).optional().nullable(),
});

export const createSupplierInvoiceInputSchema = z.object({
  supplierId: z.number().int().positive(),
  invoiceNumber: z.string().trim().min(1, "El número de factura es obligatorio").max(120),
  invoiceDate: isoDateSchema,
  expectedPaymentDate: isoDateSchema.optional().nullable(),
  taxableBase: moneySchema,
  vatAmount: moneySchema,
  totalAmount: moneySchema,
  currency: currencySchema.default("EUR"),
  source: supplierInvoiceSourceSchema.default("manual"),
  financialCategoryId: z.number().int().positive().optional().nullable(),
  categoryCode: z.string().trim().max(50).optional().nullable(),
  overrideReason: z.string().trim().max(1000).optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
  lines: z.array(supplierInvoiceLineInputSchema).default([]),
  allocations: z.array(supplierInvoiceAllocationInputSchema).default([]),
});

export const listSupplierInvoicesInputSchema = z.object({
  status: supplierInvoiceStatusSchema.optional(),
  supplierId: z.number().int().positive().optional(),
  search: z.string().trim().max(160).optional(),
  paymentMonth: paymentMonthSchema.optional(),
  paymentFrom: isoDateSchema.optional(),
  paymentTo: isoDateSchema.optional(),
  includeDeleted: z.boolean().default(false),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
}).superRefine((value, ctx) => {
  if (value.paymentFrom && value.paymentTo && value.paymentFrom > value.paymentTo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["paymentTo"],
      message: "La fecha de pago desde no puede ser posterior a la fecha hasta",
    });
  }
}).optional();

export const supplierPaymentInputSchema = z.object({
  invoiceId: z.number().int().positive(),
  paymentDate: isoDateSchema,
  amount: positiveMoneySchema,
  reference: z.string().trim().max(160).optional().nullable(),
  notes: z.string().trim().max(1000).optional().nullable(),
});

export const supplierInvoiceTransitionInputSchema = z.object({
  id: z.number().int().positive(),
  status: supplierInvoiceStatusSchema,
  reason: z.string().trim().max(1000).optional().nullable(),
});

export const supplierInvoiceSoftDeleteInputSchema = z.object({
  id: z.number().int().positive(),
  reason: z.string().trim().max(1000).optional().nullable(),
});

// Document metadata registration — server generates fileKey, validates MIME/size.
// NOTE: Binary file payload is no longer transported through this schema. The
// tRPC `supplierInvoices.uploadDocument` procedure is deprecated; uploads must
// go through the binary HTTP endpoint
// POST /api/finance/supplier-invoices/:invoiceId/documents (see server/_core/index.ts).
// The fields below remain as a metadata contract shared by the HTTP handler
// and the service-layer validation.
export const registerSupplierInvoiceDocumentInputSchema = z.object({
  invoiceId: z.number().int().positive(),
  fileName: z.string().trim().min(1).max(255).refine(
    (name) => ALLOWED_DOCUMENT_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext)),
    `Extension no permitida. Permitidas: ${ALLOWED_DOCUMENT_EXTENSIONS.join(', ')}`
  ),
  mimeType: z.string().refine(
    (val) => (ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(val),
    { message: `MIME no permitido. Permitidos: ${ALLOWED_DOCUMENT_MIME_TYPES.join(', ')}` }
  ),
  fileSize: z.number().int().positive().max(MAX_DOCUMENT_SIZE_BYTES, `El archivo excede el limite de ${MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024)} MB`),
});

// ─── TYPES ───

export type SupplierInvoiceStatus = typeof SUPPLIER_INVOICE_STATUSES[number];
export type SupplierInvoiceSource = typeof SUPPLIER_INVOICE_SOURCES[number];
export type SupplierInvoiceAllocationTarget = typeof SUPPLIER_INVOICE_ALLOCATION_TARGETS[number];
export type CreateSupplierInvoiceInput = z.infer<typeof createSupplierInvoiceInputSchema>;
export type SupplierInvoiceLineInput = z.infer<typeof supplierInvoiceLineInputSchema>;
export type SupplierInvoiceAllocationInput = z.infer<typeof supplierInvoiceAllocationInputSchema>;
export type SupplierPaymentInput = z.infer<typeof supplierPaymentInputSchema>;
export type RegisterSupplierInvoiceDocumentInput = z.infer<typeof registerSupplierInvoiceDocumentInputSchema>;

export type FinanceRole = "admin" | "admin_manager" | "project_manager" | "technician";

// ─── PERMISSIONS ───
// FINANCE PERMISSION MATRIX (FIN-01 P1)
// admin: all permissions
// admin_manager: all permissions
// project_manager: read / export only
// technician: no access (FORBIDDEN at backend)
export const FINANCE_PERMISSIONS: Record<string, Set<FinanceRole>> = {
  read: new Set<FinanceRole>(["admin", "admin_manager", "project_manager"]),
  write: new Set<FinanceRole>(["admin", "admin_manager"]),
  approve: new Set<FinanceRole>(["admin", "admin_manager"]),
  configure: new Set<FinanceRole>(["admin", "admin_manager"]),
  import: new Set<FinanceRole>(["admin", "admin_manager"]),
  export: new Set<FinanceRole>(["admin", "admin_manager", "project_manager"]),
};

// ─── STATUS TRANSITIONS ───
// MANUAL transitions are triggered via transitionStatus endpoint.
// PAYMENT-DERIVED transitions (partially_paid, paid) are set by addSupplierPayment only.
export const MANUAL_STATUS_TRANSITIONS: Record<SupplierInvoiceStatus, SupplierInvoiceStatus[]> = {
  draft: ["received", "cancelled"],
  received: ["pending_review", "cancelled"],
  pending_review: ["approved", "draft", "cancelled"],
  approved: ["scheduled_for_payment", "pending_review", "cancelled"],
  scheduled_for_payment: ["approved", "cancelled"],
  partially_paid: [],
  paid: [],
  cancelled: ["draft"],
};

export const PAYMENT_DERIVED_STATUSES = new Set<SupplierInvoiceStatus>(["partially_paid", "paid"]);

export const PAYABLE_STATUSES = new Set<SupplierInvoiceStatus>([
  "approved",
  "scheduled_for_payment",
  "partially_paid",
]);

export const TREASURY_FEED_STATUSES = new Set<SupplierInvoiceStatus>([
  "approved",
  "scheduled_for_payment",
  "partially_paid",
  "paid",
]);

export const LOCKED_HEADER_STATUSES = new Set<SupplierInvoiceStatus>([
  "approved",
  "scheduled_for_payment",
  "partially_paid",
  "paid",
  "cancelled",
]);

// Permissions required for each manual transition.
// Returns "approve" for transitions that modify the financial state.
export function requiredPermissionForSupplierInvoiceTransition(from: SupplierInvoiceStatus, to: SupplierInvoiceStatus): "write" | "approve" {
  // Any transition to approved requires approve permission
  if (to === "approved") {
    return "approve";
  }
  // Cancelling approved or scheduled_for_payment requires approve
  if (to === "cancelled" && (from === "approved" || from === "scheduled_for_payment")) {
    return "approve";
  }
  // Reverting approved back to pending_review changes financial commitment
  if (from === "approved" && to === "pending_review") {
    return "approve";
  }
  // Scheduling approved for payment
  if (from === "approved" && to === "scheduled_for_payment") {
    return "approve";
  }
  // All other manual transitions use write
  return "write";
}

// ─── ALLOWED ALLOCATION RULES ───
// Which target types require targetId
export function allocationTargetRequiresId(targetType: SupplierInvoiceAllocationTarget): boolean {
  return targetType === "installation";
}

export function allocationTargetForbidsId(targetType: SupplierInvoiceAllocationTarget): boolean {
  return targetType !== "installation";
}

// ─── STATUS TRANSITION HELPERS ───

export function assertSupplierInvoiceTransition(from: SupplierInvoiceStatus, to: SupplierInvoiceStatus) {
  if (from === to) return;
  if (!MANUAL_STATUS_TRANSITIONS[from]?.includes(to)) {
    if (PAYMENT_DERIVED_STATUSES.has(to)) {
      throw Object.assign(
        new Error(`SUPPLIER_INVOICE_TRANSITION_PAYMENT_DERIVED: El estado "${to}" solo puede establecerse mediante pagos.`),
        { code: "BAD_REQUEST" },
      );
    }
    throw Object.assign(
      new Error(`SUPPLIER_INVOICE_INVALID_STATUS_TRANSITION: No se puede pasar de "${from}" a "${to}".`),
      { code: "BAD_REQUEST" },
    );
  }
}

// ─── HELPERS ───

export const SUPPLIER_INVOICE_STATUS_TRANSITIONS: Record<SupplierInvoiceStatus, SupplierInvoiceStatus[]> = {
  ...MANUAL_STATUS_TRANSITIONS,
  // Add payment-derived transitions for completeness in read-only lookup
  approved: [...(MANUAL_STATUS_TRANSITIONS.approved || []), "partially_paid", "paid"],
  scheduled_for_payment: [...(MANUAL_STATUS_TRANSITIONS.scheduled_for_payment || []), "partially_paid", "paid"],
  partially_paid: ["paid"],
};

export function normalizeDecimalString(value: unknown): string {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "NaN";
    return String(value);
  }
  const raw = String(value ?? "")
    .trim()
    .replace(/\s/g, "");
  if (!raw) return "";
  if (raw.includes(",")) {
    return raw.replace(/\./g, "").replace(",", ".");
  }
  return raw;
}

export function parseMoneyToCents(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const normalized = normalizeDecimalString(value);
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const [eurosRaw, centsRaw = ""] = normalized.split(".");
  const sign = eurosRaw.startsWith("-") ? -1 : 1;
  const euros = Math.abs(Number(eurosRaw));
  const cents = Number(centsRaw.padEnd(2, "0"));
  if (!Number.isInteger(euros) || !Number.isInteger(cents)) return null;
  return sign * (euros * 100 + cents);
}

export function formatCentsAsDecimal(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}

export function sumMoneyCents(values: Array<unknown>): number {
  return values.reduce<number>((total, value) => {
    const cents = parseMoneyToCents(value);
    if (cents === null) throw new Error("INVALID_MONEY");
    return total + cents;
  }, 0);
}

export function assertSupplierInvoicePermission(role: FinanceRole, action: "read" | "write" | "approve" | "configure" | "import" | "export") {
  const allowed = FINANCE_PERMISSIONS[action];
  if (!allowed || !allowed.has(role)) {
    throw Object.assign(new Error("FINANCE_SUPPLIER_INVOICE_FORBIDDEN"), { code: "FORBIDDEN" });
  }
}

/** Distribute invoice base and VAT over allocation amounts in cents. Each bucket
 * is rounded down first, then remaining cents are assigned by largest remainder;
 * consequently every row and both invoice totals reconcile exactly. */
export function distributeInvoiceAmountsByAllocation<T extends { allocatedAmount: unknown }>(
  taxableBase: unknown, vatAmount: unknown, allocations: T[],
): Array<T & { allocatedTaxableBase: string; allocatedVatAmount: string }> {
  const base = parseMoneyToCents(taxableBase);
  const vat = parseMoneyToCents(vatAmount);
  const weights = allocations.map((row) => parseMoneyToCents(row.allocatedAmount));
  if (base === null || vat === null || weights.some((n) => n === null) || weights.some((n) => n! < 0)) throw new Error("INVALID_ALLOCATION_DISTRIBUTION");
  const denominator = weights.reduce<number>((sum, n) => sum + n!, 0);
  if (denominator === 0) return allocations.map((row) => ({ ...row, allocatedTaxableBase: "0.00", allocatedVatAmount: "0.00" }));
  const distribute = (total: number) => {
    const totalBig = BigInt(total);
    const denominatorBig = BigInt(denominator);
    const raw = weights.map((weight, index) => {
      const numerator = totalBig * BigInt(weight!);
      return { index, quotient: Number(numerator / denominatorBig), remainder: numerator % denominatorBig };
    });
    let centsLeft = total - raw.reduce((sum, row) => sum + row.quotient, 0);
    raw.slice().sort((a, b) => a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1).slice(0, centsLeft).forEach((row) => { raw[row.index]!.quotient += 1; });
    return raw.map((row) => row.quotient);
  };
  const bases = distribute(base);
  const vats = distribute(vat);
  return allocations.map((row, index) => ({ ...row, allocatedTaxableBase: formatCentsAsDecimal(bases[index]!), allocatedVatAmount: formatCentsAsDecimal(vats[index]!) }));
}

export function assertAllocationsReconcile(totalAmount: unknown, allocations: Array<{ allocatedAmount: unknown }>) {
  if (allocations.length === 0) return;
  const total = parseMoneyToCents(totalAmount);
  if (total === null) throw new Error("INVALID_TOTAL_AMOUNT");
  const allocated = sumMoneyCents(allocations.map((allocation) => allocation.allocatedAmount));
  if (allocated !== total) {
    throw Object.assign(new Error("SUPPLIER_INVOICE_ALLOCATIONS_DO_NOT_RECONCILE"), {
      code: "BAD_REQUEST",
      expectedCents: total,
      actualCents: allocated,
    });
  }
}

export function assertLineTotalsReconcile(totalAmount: unknown, lines: Array<{ totalAmount: unknown }>) {
  if (lines.length === 0) return;
  const total = parseMoneyToCents(totalAmount);
  if (total === null) throw new Error("INVALID_TOTAL_AMOUNT");
  const lineTotal = sumMoneyCents(lines.map((line) => line.totalAmount));
  if (lineTotal !== total) {
    throw Object.assign(new Error("SUPPLIER_INVOICE_LINES_DO_NOT_RECONCILE"), {
      code: "BAD_REQUEST",
      expectedCents: total,
      actualCents: lineTotal,
    });
  }
}

export function deriveSupplierPaymentSummary(invoiceTotal: unknown, payments: Array<{ amount: unknown; deletedAt?: unknown }>) {
  const totalCents = parseMoneyToCents(invoiceTotal);
  if (totalCents === null) throw new Error("INVALID_TOTAL_AMOUNT");
  const paidCents = sumMoneyCents(payments.filter((payment) => !payment.deletedAt).map((payment) => payment.amount));
  if (paidCents > totalCents) {
    throw Object.assign(new Error("SUPPLIER_INVOICE_OVERPAYMENT"), {
      code: "BAD_REQUEST",
      totalCents,
      paidCents,
    });
  }
  return {
    totalAmount: formatCentsAsDecimal(totalCents),
    paidAmount: formatCentsAsDecimal(paidCents),
    pendingAmount: formatCentsAsDecimal(totalCents - paidCents),
    paidCents,
    pendingCents: totalCents - paidCents,
    paymentStatus: paidCents === 0 ? "unpaid" : paidCents === totalCents ? "paid" : "partially_paid",
  } as const;
}

export function deriveStatusAfterPayment(currentStatus: SupplierInvoiceStatus, invoiceTotal: unknown, payments: Array<{ amount: unknown; deletedAt?: unknown }>): SupplierInvoiceStatus {
  if (currentStatus === "cancelled") return "cancelled";
  const summary = deriveSupplierPaymentSummary(invoiceTotal, payments);
  if (summary.paymentStatus === "paid") return "paid";
  if (summary.paymentStatus === "partially_paid") return "partially_paid";
  return currentStatus;
}

export function isSupplierInvoiceVisible(invoice: { deletedAt?: unknown }, includeDeleted = false): boolean {
  return includeDeleted || !invoice.deletedAt;
}

// Normmalize invoice number for duplicate detection: trim, uppercase.
export function normalizeInvoiceNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.trim().toUpperCase();
}